import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';

import { checkValidToken } from '../../actions/session.action';
import Layout from '../Layout';

const qs = require('query-string');

// react-router v6 guard. Renders the protected element wrapped in Layout when
// authorized, otherwise redirects.
// Usage: <PrivateRoute adminRoute><Page/></PrivateRoute> (SUPER_ADMIN only)
//        <PrivateRoute adminRoute orgAdmin><Page/></PrivateRoute> (also ORGANIZATION_ADMIN)
const PrivateRoute = ({ children, adminRoute, orgAdmin }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const returnUrl =
    location.pathname === '/'
      ? '/signup'
      : `/login?returnUrl=${location.pathname}${location.search}`;

  // 1. Look for a jwt on the store
  const storeJwt = useSelector((state) => state.session.jwt);
  if (storeJwt) {
    if (storeJwt !== 'error') {
      const jwtObj = jwtDecode(storeJwt);
      if (!adminRoute) {
        return <Layout>{children}</Layout>;
      }
      const allowedRoles = orgAdmin ? ['SUPER_ADMIN', 'ORGANIZATION_ADMIN'] : ['SUPER_ADMIN'];
      if (allowedRoles.includes(jwtObj.role)) {
        return <Layout>{children}</Layout>;
      }
      // Authenticated but lacking rights for this admin route. Send home rather
      // than to /login: the user IS logged in, so /login would just navigate
      // back here (via returnUrl) and create an infinite redirect loop.
      return <Navigate to="/" replace />;
    }
    // Token present but invalid/expired -> go to /login to re-authenticate.
    return <Navigate to={returnUrl} replace />;
  }

  // 2. If there is no jwt on the store, look it on the session
  const sessionJwt = sessionStorage.getItem('jwt');
  if (sessionJwt) {
    dispatch(checkValidToken(sessionJwt));
    return null;
  }

  // 3. If there is no jwt on the session, look it on url params.
  const params = qs.parse(location.search);
  const { jwt } = params;
  if (!jwt) {
    return <Navigate to={returnUrl} replace />;
  }
  dispatch(checkValidToken(jwt));
  return null;
};
PrivateRoute.defaultProps = {
  adminRoute: false,
  orgAdmin: false,
};

PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired,
  adminRoute: PropTypes.bool,
  orgAdmin: PropTypes.bool,
};

export default PrivateRoute;
